import React, { ReactNode, useState } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import useTheme from "../../../../common/hooks/useTheme";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import ValidationErrorDisplay from "./ValidationErrorDisplay";

// Custom styled component for segmented progress bar
const SegmentedProgress = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5), // Small gap between segments
  marginBottom: "32px",
  "& .segment": {
    height: 4,
    flex: 1,
    borderRadius: theme.spacing(2), // Full border radius for pill shape
    transition: "background-color 0.3s ease-in-out",
  },
}));

const SegmentedStepper = ({
  currentStep,
  setCurrentStep,
  children,
  isNextButtonDisabled,
  selectedServiceType,
  handleSave,
  isSaveDisabled = false,
  startStep = 1,
  totalSteps = 3,
  isEditMode = false,
  onStepValidation,
  onStepChange,
  validationErrors = [],
  onDelete,
}: {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  children: ReactNode;
  isNextButtonDisabled: boolean;
  selectedServiceType?: string | null;
  handleSave: () => void;
  isSaveDisabled?: boolean;
  startStep?: number;
  totalSteps?: number;
  isEditMode?: boolean;
  onStepValidation?: (step: number) => boolean;
  onStepChange?: (newStep: number, direction: "forward" | "back") => void;
  validationErrors?: string[];
  onDelete?: () => void;
}) => {
  const theme = useTheme();

  const handleNext = () => {
    // Validate current step before proceeding
    if (onStepValidation) {
      const isValid = onStepValidation(currentStep);
      if (!isValid) {
        return; // Don't proceed if validation fails
      }
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > startStep) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      onStepChange?.(newStep, "back");
    }
  };

  const handleSaveWithValidation = () => {
    // Validate current step before saving (on the final step)
    if (currentStep === totalSteps && onStepValidation) {
      const isValid = onStepValidation(currentStep);
      if (!isValid) {
        return; // Don't save if validation fails
      }
    }

    handleSave();
  };

  return (
    <Box
      sx={{
        maxWidth: "100%",
        height: "100%",
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.75,
        }}
      >
        {/* Step Counter */}
        <Typography
          sx={{
            fontFamily: "var(--font-Medium)",
            fontSize: "12px",
            lineHeight: "17px",
            color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
          }}
        >
          {currentStep - startStep + 1} to {totalSteps - startStep + 1} steps
        </Typography>
        {(currentStep !== startStep || isEditMode) && selectedServiceType && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "12px",
              lineHeight: "17px",
              color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-Medium)",
              }}
            >
              Selected service:
            </Typography>
            <Typography sx={{ fontFamily: "var(--font-Bold)" }}>
              {selectedServiceType}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Segmented Progress Bar */}
      <SegmentedProgress>
        {Array.from({ length: totalSteps - startStep + 1 }, (_, index) => (
          <div
            key={index}
            className={`segment ${
              index < currentStep - startStep + 1 ? "active" : ""
            }`}
            style={{
              backgroundColor:
                index < currentStep - startStep + 1
                  ? theme.colors_ds.PRIMARY_ICON_MAIN
                  : theme.colors_ds.NEUTRAL_BORDER_WEAK,
            }}
          />
        ))}
      </SegmentedProgress>

      {/* Content Area - grows to fill available space */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column" as "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {children}
      </Box>

      {/* Navigation Buttons - stays at bottom */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mt: 1,
          gap: 1,
        }}
      >
        {/* Validation Errors Display */}
        <ValidationErrorDisplay errors={validationErrors} />

        {/* Button Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left side - Delete button */}
          <Box>
            {onDelete && (
              <PrimaryButton
                label={"Delete"}
                onClick={onDelete}
                sx={{
                  backgroundColor: theme.colorTokens.D[50],
                  "&:hover": {
                    backgroundColor: theme.colorTokens.D[60],
                  },
                }}
              />
            )}
          </Box>

          {/* Right side - Navigation buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {currentStep !== startStep && (
              <SecondaryButton label={"Previous"} onClick={handleBack} />
            )}
            {currentStep === totalSteps ? (
              <PrimaryButton
                label={"Save"}
                onClick={handleSaveWithValidation}
                isDisable={isSaveDisabled}
              />
            ) : (
              <PrimaryButton
                label={"Next"}
                onClick={handleNext}
                isDisable={isNextButtonDisabled}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SegmentedStepper;
